import React, { useEffect, useRef } from 'react';
import { Box, Typography, Button, Divider, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState, useRecoilCallback } from 'recoil';
import { atom_routeToStopLocation, atom_isDesktop, atom_isNavigationMode, atom_showNavigationBubble, atom_currentNavigationStep, atom_fitBoundsRequest, atom_tourState } from '@app/shared/state/tour.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { useResizablePanel } from '@app/shared/hooks/useResizablePanel';
import { safePushState, safeHistoryBack } from '@app/shared/utils/safe-history';

// Get icon class based on travel_mode and html_instructions
function getTravelModeIcon(step: any) {
  const instr = (step.html_instructions || '').toLowerCase().trim();
  if (step.travel_mode === 'WALKING' || instr.includes('walk')) return 'fa-person-walking';
  if (step.travel_mode === 'TRANSIT' && step.transit_details?.line?.vehicle?.type === 'BUS' || instr.includes('bus')) return 'fa-bus';
  if (step.travel_mode === 'TRANSIT' && step.transit_details?.line?.vehicle?.type === 'HEAVY_RAIL' || instr.includes('train')) return 'fa-train';
  if (step.travel_mode === 'TRANSIT' && step.transit_details?.line?.vehicle?.type === 'SUBWAY' || instr.includes('subway')) return 'fa-subway';
  return 'fa-flag';
}

// Get direction icon or text for non-transit steps (walking, driving, cycling)
function getDirectionIcon(htmlInstructions: string): { type: 'icon' | 'text'; value: string } {
  // Strip HTML tags and convert to lowercase
  const instr = (htmlInstructions || '').replace(/<[^>]*>/g, '').toLowerCase();

  // Multi-language patterns
  // Left: en=left, fr=gauche, he=שמאלה, pt=esquerda, es=izquierda, de=links, it=sinistra
  const leftPattern = /\b(left|gauche|esquerda|izquierda|links|sinistra)\b|שמאלה/;
  // Right: en=right, fr=droite, he=ימינה, pt=direita, es=derecha, de=rechts, it=destra
  const rightPattern = /\b(right|droite|direita|derecha|rechts|destra)\b|ימינה/;
  // Turn: en=turn, fr=tournez/tourner/tournera/prendre, he=פנה/פניה, pt=vire, es=gire, de=abbiegen, it=svolta
  const turnPattern = /\b(turn|tournez|tourner|tournera|prendre|vire|gire|abbiegen|svolta|slight|sharp|bear|légèrement|fortement)\b|פנה|פניה/;
  // U-turn
  const uTurnPattern = /u-turn|u turn|uturn|demi-tour|inversão|cambio de sentido|פניית פרסה/;
  // Straight/Continue: en=continue/straight/keep, fr=continuez/continuer/tout droit, he=ישר/המשך, pt=continue/reto/em frente
  const straightPattern = /\b(continue|straight|keep|continuez|continuer|tout\s+droit|reto|em\s+frente|siga|continúe|recto|weiter|geradeaus|continua|dritto)\b|ישר|המשך/;
  // Merge
  const mergePattern = /\b(merge|fusionner|fundir|fusionar|einordnen|unire)\b|התמזג/;
  // Roundabout
  const roundaboutPattern = /\b(roundabout|rotary|circle|rond-point|rotatória|rotonda|kreisverkehr|rotonda)\b|כיכר/;
  // Destination/Arrive
  const destinationPattern = /\b(destination|arrive|arriving|arrivée|destino|chegada|llegada|ziel|destinazione)\b|יעד|הגעה/;

  // Check for turn directions
  if (leftPattern.test(instr) && turnPattern.test(instr)) {
    return { type: 'icon', value: 'fa-arrow-left' };
  }
  if (rightPattern.test(instr) && turnPattern.test(instr)) {
    return { type: 'icon', value: 'fa-arrow-right' };
  }
  if (uTurnPattern.test(instr)) {
    return { type: 'icon', value: 'fa-rotate-left' };
  }
  if (straightPattern.test(instr)) {
    return { type: 'icon', value: 'fa-arrow-up' };
  }
  if (mergePattern.test(instr)) {
    return { type: 'icon', value: 'fa-code-merge' };
  }
  if (roundaboutPattern.test(instr)) {
    return { type: 'icon', value: 'fa-rotate-right' };
  }
  if (destinationPattern.test(instr)) {
    return { type: 'icon', value: 'fa-flag-checkered' };
  }

  // Cardinal directions - multi-language (check compound first)
  if (/\b(northeast|nord-est|nordeste|noreste|nordost|nord-est)\b|צפון-מזרח|\bne\b/.test(instr)) {
    return { type: 'text', value: 'NE' };
  }
  if (/\b(northwest|nord-ouest|noroeste|nordwest|nord-ovest)\b|צפון-מערב|\bnw\b/.test(instr)) {
    return { type: 'text', value: 'NW' };
  }
  if (/\b(southeast|sud-est|sudeste|sureste|südost|sud-est)\b|דרום-מזרח|\bse\b/.test(instr)) {
    return { type: 'text', value: 'SE' };
  }
  if (/\b(southwest|sud-ouest|sudoeste|suroeste|südwest|sud-ovest)\b|דרום-מערב|\bsw\b/.test(instr)) {
    return { type: 'text', value: 'SW' };
  }
  if (/\b(north|nord|norte|norden)\b|צפונה|צפון/.test(instr)) {
    return { type: 'text', value: 'N' };
  }
  if (/\b(south|sud|sul|süden)\b|דרומה|דרום/.test(instr)) {
    return { type: 'text', value: 'S' };
  }
  if (/\b(east|est|leste|este|osten)\b|מזרחה|מזרח/.test(instr)) {
    return { type: 'text', value: 'E' };
  }
  if (/\b(west|ouest|oeste|westen|ovest)\b|מערבה|מערב/.test(instr)) {
    return { type: 'text', value: 'W' };
  }

  // Default - location marker
  return { type: 'icon', value: 'fa-location-dot' };
}

function formatLineShortName(shortName: string | undefined): string {
  if (!shortName) return '';
  if (/line/i.test(shortName)) {
    return shortName;
  }
  const numberMatch = shortName.match(/\d+/);
  return numberMatch ? numberMatch[0] : shortName;
}

// Format step instruction (same logic as DirectionsContent)
function getStepInstruction(step: any, nextStep?: any): string {
  if (step.travel_mode === 'WALKING' || (step.html_instructions || '').toLowerCase().includes('walk')) {
    if (nextStep && nextStep.transit_details && nextStep.transit_details.departure_stop?.name) {
      return `walk to ${nextStep.transit_details.departure_stop.name}`;
    }
    return step.html_instructions;
  }

  if (step.travel_mode === 'TRANSIT' && step.html_instructions.includes('Bus')) {
    const lineName = step.transit_details.line?.short_name;
    const formattedLine = lineName ? (/line/i.test(lineName) ? lineName : `line ${formatLineShortName(lineName)}`) : '';
    return `Take bus${formattedLine ? ` ${formattedLine}` : ''} towards ${step.transit_details.arrival_stop?.name || ''}`;
  }

  if (step.travel_mode === 'TRANSIT' && step.html_instructions.includes('Train')) {
    const lineName = step.transit_details.line?.short_name;
    const formattedLine = lineName ? (/line/i.test(lineName) ? lineName : `line ${formatLineShortName(lineName)}`) : '';
    return `Take train${formattedLine ? ` ${formattedLine}` : ''} towards ${step.transit_details.arrival_stop?.name || ''}`;
  }

  if (step.travel_mode === 'TRANSIT' && step.html_instructions.includes('Subway')) {
    const lineName = step.transit_details.line?.short_name;
    const formattedLine = lineName ? (/line/i.test(lineName) ? lineName : `line ${formatLineShortName(lineName)}`) : '';
    return `Take subway${formattedLine ? ` ${formattedLine}` : ''} towards ${step.transit_details.arrival_stop?.name || ''}`;
  }

  return step.html_instructions || '';
}

// Helper to check if a departure time is today or later
function getDepartureInfo(step: any) {
  if (step.travel_mode !== 'TRANSIT' || !step.transit_details?.departure_time) {
    return null;
  }
  const departureTime = step.transit_details.departure_time;
  const departureDate = new Date(departureTime.value * 1000);
  const now = new Date();
  const isToday = departureDate.toDateString() === now.toDateString();
  const isTomorrow = departureDate.toDateString() === new Date(now.getTime() + 86400000).toDateString();

  return {
    text: departureTime.text,
    date: departureDate,
    isToday,
    isTomorrow,
    isLater: !isToday && !isTomorrow,
  };
}

// Get color for non-transit travel mode
function getModeColor(travelMode?: string) {
  switch (travelMode) {
    case 'walking': return '#1976D2';
    case 'bicycling': return '#F57C00';
    case 'driving': return '#C2185B';
    default: return '#D25529';
  }
}

interface RouteListContentProps {
  contentRef: React.RefObject<HTMLDivElement>;
  preventMapInteraction: (e: React.MouseEvent | React.TouchEvent) => void;
  initiallyVisible?: boolean;
}

export const RouteListContent: React.FC<RouteListContentProps> = ({
  contentRef,
  preventMapInteraction,
  initiallyVisible,
}) => {
  const routeToStopState = useRecoilValue(atom_routeToStopLocation);
  const setRouteToStopLocation = useSetRecoilState(atom_routeToStopLocation);
  const setIsNavigationMode = useSetRecoilState(atom_isNavigationMode);
  const setShowNavigationBubble = useSetRecoilState(atom_showNavigationBubble);
  const currentNavigationStep = useRecoilValue(atom_currentNavigationStep);
  const setCurrentNavigationStep = useSetRecoilState(atom_currentNavigationStep);
  const setFitBoundsRequest = useSetRecoilState(atom_fitBoundsRequest);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const isExpanded = true; // always expanded; collapse removed
  const isDesktop = useRecoilValue(atom_isDesktop);
  const setListMode = useSetRecoilState(atom_listModeState);
  // Mirror MobileNavigationContent's default — drop user back into the device-appropriate list
  // so the active stop's MapCard is visible on minimize / end-ride. Read tour state on-demand
  // via snapshot to avoid subscribing this component to unrelated tour-state changes.
  const getDefaultListMode = useRecoilCallback(({ snapshot }) => () => {
    if (isDesktop) return 'desktop' as const;
    const tour = snapshot.getLoadable(atom_tourState).contents as { config?: Record<string, unknown> } | null;
    if (tour?.config?.['show_list']) return 'mobileList' as const;
    return 'mobileSwiper' as const;
  }, [isDesktop]);

  // Handle mobile back button - close route on popstate
  const closedByBackRef = useRef(false);
  useEffect(() => {
    if (!routeToStopState) return;

    safePushState({ routeToStop: true });
    closedByBackRef.current = false;

    // Ignore popstate events fired in the same render-tick as our mount.
    // BigStopPage's cleanup calls history.back() when openedStopState is cleared
    // by the navigation flow; that pop would otherwise tear down the route we just set.
    let ignoreFirstPops = true;
    const settleTimer = setTimeout(() => { ignoreFirstPops = false; }, 250);

    const onPopState = () => {
      if (ignoreFirstPops) {
        console.log('[ROUTE-DEBUG] ignoring popstate during settle window');
        return;
      }
      closedByBackRef.current = true;
      setRouteToStopLocation(null);
      setIsNavigationMode(false);
      setShowNavigationBubble(false);
      setCurrentNavigationStep(0);
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      clearTimeout(settleTimer);
      window.removeEventListener('popstate', onPopState);
      if (!closedByBackRef.current) {
        safeHistoryBack();
      }
    };
  }, [routeToStopState?.stopTitle, setRouteToStopLocation, setIsNavigationMode, setShowNavigationBubble, setCurrentNavigationStep]);

  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const minHeight = Math.floor(viewportHeight * 0.25);
  const maxHeight = Math.floor(viewportHeight * 0.8);
  const hasInitializedHeightRef = useRef(false);

  const { currentHeight, setCurrentHeight, isDragging, isMaxHeight, setIsMaxHeight, dragHandleProps, containerTransitionStyle, startExit } = useResizablePanel({
    minHeight,
    maxHeight,
    initialHeight: Math.floor(viewportHeight * 0.4),
    initiallyVisible,
  });

  // Set initial height to 40% when route exists
  useEffect(() => {
    if (routeToStopState && !hasInitializedHeightRef.current) {
      const initialHeight = Math.floor(viewportHeight * 0.4);
      setCurrentHeight(initialHeight);
      hasInitializedHeightRef.current = true;
    }
    if (!routeToStopState) {
      hasInitializedHeightRef.current = false;
    }
  }, [routeToStopState, viewportHeight, setCurrentHeight]);

  // Narrow first leg and steps for safe access
  const firstLeg = Array.isArray(routeToStopState?.legs) && (routeToStopState?.legs?.length ?? 0) > 0 ? routeToStopState?.legs[0] : undefined;
  const steps = firstLeg && Array.isArray(firstLeg.steps) ? firstLeg.steps : undefined;

  // Check if any transit step departs tomorrow or later
  const firstTransitDeparture = steps?.find((s: any) => s.travel_mode === 'TRANSIT' && s.transit_details?.departure_time);
  const transitDepartureInfo = firstTransitDeparture ? getDepartureInfo(firstTransitDeparture) : null;

  // Calculate if ride is more than 1 hour away (walking time + 5 min precaution before first transit)
  const getTimeUntilDeparture = () => {
    if (!steps || !firstTransitDeparture) return null;

    // Find walking step before first transit (if any)
    const firstTransitIndex = steps.findIndex((s: any) => s.travel_mode === 'TRANSIT');
    const walkingStepBefore = firstTransitIndex > 0 ? steps[firstTransitIndex - 1] : null;
    const walkingTimeSeconds = walkingStepBefore?.duration?.value || 0;

    // Get first transit departure time
    const departureTimestamp = firstTransitDeparture.transit_details?.departure_time?.value;
    if (!departureTimestamp) return null;

    // Calculate when user should leave: departure time - walking time - 5 min precaution
    const precautionSeconds = 5 * 60;
    const shouldLeaveAt = (departureTimestamp * 1000) - (walkingTimeSeconds * 1000) - (precautionSeconds * 1000);
    const now = Date.now();
    const timeUntilLeaveMs = shouldLeaveAt - now;

    return {
      isMoreThanHour: timeUntilLeaveMs > 60 * 60 * 1000,
      timeUntilLeaveMs,
    };
  };

  const timeUntilDeparture = getTimeUntilDeparture();

  // Some properties are added at runtime (stopTitle / stopImage / travelMode) — access safely via `any` to avoid strict type errors
  const stopImage = (routeToStopState as any)?.stopImage as string | undefined;
  const stopTitle = (routeToStopState as any)?.stopTitle as string | undefined;
  const travelMode = (routeToStopState as any)?.travelMode as 'transit' | 'walking' | 'bicycling' | 'driving' | undefined;
  const isTransitMode = !travelMode || travelMode === 'transit';

  const modeColor = getModeColor(travelMode);

  // Render content box (shared between desktop and mobile)
  const contentBox = (
    <Box
      dir={isRTL ? 'rtl' : 'ltr'}
      sx={{
        boxShadow: !isDesktop ? '0px 0px 10px 0px rgba(0,0,0,0.15)' : 'none',
        bgcolor: 'white',
        alignSelf: 'center',
        position: !isDesktop ? 'fixed' : 'relative',
        zIndex: 500,
        display: 'flex',
        width: '100%',
        maxWidth: isDesktop ? '530px' : '100%',
        flexDirection: 'column',
        pt: 2,
        px: { xs: 1.5, md: 3 },
        borderRadius: !isDesktop ? '20px 20px 0px 0px' : '0px',
        bottom: !isDesktop ? 0 : 'auto',
        height: isDesktop ? '100%' : `${currentHeight}px`,
        ...(isDesktop ? {} : containerTransitionStyle),
        touchAction: 'pan-y',
        pointerEvents: 'auto',
        isolation: 'isolate',
        overflowX: 'hidden',
      }}
      onClick={preventMapInteraction}
      onTouchStart={preventMapInteraction}
      onTouchMove={preventMapInteraction}
      onMouseDown={preventMapInteraction}
      onMouseMove={preventMapInteraction}
    >
      {/* Drag handle — sits inside the panel so it anchors to the panel top and
          so useResizablePanel's parentElement-based height write targets this box. */}
      {!isDesktop && (
        <Box
          {...dragHandleProps}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'ns-resize',
            touchAction: 'none',
            padding: '8px 50px',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(95,93,92,1)',
              height: '6px',
              width: '60px',
              borderRadius: '999px',
            }}
          />
        </Box>
      )}
      {/* Static Close & Minimize buttons — pinned to panel top, outside the scroll area */}
      <Box
        sx={{
          position: 'absolute',
          top: !isDesktop ? 24 : 12,
          ...(isRTL ? { left: 12 } : { right: 12 }),
          display: 'flex',
          gap: 0.5,
          zIndex: 20,
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setCurrentHeight(minHeight);
            setIsMaxHeight(false);
            setIsNavigationMode(false);
            setShowNavigationBubble(true);
            setListMode(getDefaultListMode());
          }}
          aria-label={t('minimize') || 'Minimize'}
          size="small"
          sx={{
            width: 28,
            height: 28,
            bgcolor: '#F6F6F9',
            '&:hover': { bgcolor: '#e8e8eb' },
          }}
        >
          <i className="fa-solid fa-minus" style={{ fontSize: 12, color: '#333' }} />
        </IconButton>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            startExit(() => {
              setRouteToStopLocation(null);
              setIsNavigationMode(false);
              setShowNavigationBubble(false);
              setCurrentNavigationStep(0);
              setListMode(getDefaultListMode());
            });
          }}
          aria-label={t('navigation.end_ride') || 'Close'}
          size="small"
          sx={{
            width: 28,
            height: 28,
            bgcolor: '#F6F6F9',
            '&:hover': { bgcolor: '#e8e8eb' },
          }}
        >
          <i className="fa-solid fa-xmark" style={{ fontSize: 14, color: '#333' }} />
        </IconButton>
      </Box>
      <Box
        component="div"
        ref={contentRef}
        role="region"
        aria-label={t('accessibility.route_directions') as string}
        sx={{
          zIndex: 10,
          width: '100%',
          mt: 2,
          pb: '70px', // Padding for bottom bar
          overflowY: 'auto',
          display: 'block',
          // maxHeight: `${currentHeight - 80}px`,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          pointerEvents: 'auto',
          touchAction: 'pan-y',
        }}
        onClick={preventMapInteraction}
        onTouchStart={preventMapInteraction}
        onTouchMove={preventMapInteraction}
        onMouseDown={preventMapInteraction}
        onMouseMove={preventMapInteraction}
      >
        {routeToStopState && Array.isArray(routeToStopState.legs) && routeToStopState.legs.length > 0 ? (
          <Box
            sx={{ width: '100%' }}
          >
            {/* Part 1: Stop Info - Image, Name, Distance/Time */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                pb: '12px',
                borderRadius: '8px',
                position: 'relative',
              }}
            >
              {/* Stop Image — only rendered when present so the title fills the leading edge otherwise */}
              {stopImage && (
                <Box sx={{ position: 'relative', minWidth: '44px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', pt: 0.5 }}>
                  <Box
                    sx={{
                      width: 'auto',
                      height: '70px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={stopImage}
                      alt={stopTitle || 'Stop'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                </Box>
              )}
              {/* Stop Name and Distance/Time */}
              <Box sx={{ flex: 1, pt: 0.5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    textAlign: 'start',
                    mb: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                    maxWidth: '100%',
                  }}
                >
                  {stopTitle || 'Directions to stop'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    color: '#48526B',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block',
                    maxWidth: '100%'
                  }}
                >
                  Total distance: {firstLeg?.distance?.text} {' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      fontStyle: 'normal',
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '0px',
                      color: '#D25529',
                      whiteSpace: 'nowrap',
                      display: 'inline-block'
                    }}
                  >
                    ({firstLeg?.duration?.text})
                  </Box>
                </Typography>

              </Box>

            </Box>

            {/* Part 2: Actions - Route Options, Buttons, Warnings */}
            <Box sx={{ width: '100%', pb: 1.5 }}>

              {/* Route alternatives selector — compact numbered pills in a single row */}
              {routeToStopState?.allRoutes && routeToStopState.allRoutes.length > 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.75, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {routeToStopState.allRoutes.map((route, index) => {
                    const isSelected = index === (routeToStopState.selectedRouteIndex ?? 0);
                    return (
                      <Button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const selectedRoute = routeToStopState.allRoutes![index];
                          setRouteToStopLocation({
                            ...selectedRoute,
                            stopImage: routeToStopState.stopImage,
                            stopTitle: routeToStopState.stopTitle,
                            allRoutes: routeToStopState.allRoutes,
                            selectedRouteIndex: index,
                            tourId: (routeToStopState as any).tourId,
                          });
                          setCurrentNavigationStep(0);
                        }}
                        variant={isSelected ? 'contained' : 'outlined'}
                        disableRipple
                        aria-label={`${t('route') || 'Route'} ${index + 1}`}
                        sx={{
                          minWidth: '28px',
                          width: '28px',
                          height: '28px',
                          padding: 0,
                          color: isSelected ? '#fff' : '#48526B',
                          borderColor: isSelected ? '#D25529' : '#ddd',
                          backgroundColor: isSelected ? '#D25529' : 'transparent',
                          fontWeight: 600,
                          fontSize: '13px',
                          lineHeight: 1,
                          textTransform: 'none',
                          borderRadius: '50%',
                          '&:hover': {
                            backgroundColor: isSelected ? '#b8421e' : 'rgba(0,0,0,0.04)',
                            borderColor: isSelected ? '#b8421e' : '#ccc',
                          },
                        }}
                      >
                        {index + 1}
                      </Button>
                    );
                  })}
                </Box>
              )}

              {/* Warning if transit not available today */}
              {transitDepartureInfo && !transitDepartureInfo.isToday && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1.5,
                    p: 1,
                    bgcolor: '#FFF3E0',
                    borderRadius: '8px',
                    border: '1px solid #FFB74D',
                  }}
                >
                  <i className="fa-solid fa-clock" style={{ color: '#F57C00', fontSize: '14px' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      color: '#E65100',
                      fontWeight: 500,
                    }}
                  >
                    {transitDepartureInfo.isTomorrow
                      ? `Next available transit departs tomorrow at ${transitDepartureInfo.text}`
                      : `Next available transit departs on ${transitDepartureInfo.date.toLocaleDateString()} at ${transitDepartureInfo.text}`}
                  </Typography>
                </Box>
              )}

              {/* Notice if ride is more than 1 hour away */}
              {timeUntilDeparture?.isMoreThanHour && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1.5,
                    fontSize: '13px',
                    color: '#666',
                    fontStyle: 'italic',
                  }}
                >
                  This ride is more than an hour away
                </Typography>
              )}
            </Box>

            <Divider sx={{ width: '100%', mb: 1.5 }} />

            {/* Step-by-step directions (always visible) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              {steps
                // For non-transit modes, filter to show only previous, current, and next step
                ?.filter((step: any, i: number) => {
                  if (isTransitMode) return true; // Show all steps for transit
                  // Show previous step, current step, and next step
                  return i >= currentNavigationStep - 1 && i <= currentNavigationStep + 1;
                })
                .map((step: any, filteredIndex: number, filteredArr: any[]) => {
                // Find original index in the full steps array
                const originalIndex = steps?.findIndex((s: any) => s === step) ?? filteredIndex;
                const i = originalIndex;
                const arr = steps || [];
                const nextStep = arr[i + 1];
                const instruction = getStepInstruction(step, nextStep);
                const transitIcon = getTravelModeIcon(step);
                const directionIcon = getDirectionIcon(step.html_instructions);
                const isWalking = step.travel_mode === 'WALKING' || (step.html_instructions || '').toLowerCase().includes('walk');
                const isCurrentStep = i === currentNavigationStep;
                const isLastInFiltered = filteredIndex === filteredArr.length - 1;

                return (
                  <>
                    <Box
                      key={i}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      sx={{
                        mb: isLastInFiltered ? 0 : 1.5,
                        width: '100%',
                        height: 'auto',
                        transform: 'rotate(0deg)',
                        opacity: 1,
                        boxSizing: 'border-box',
                        bgcolor: isCurrentStep ? '#FFF3E0' : 'transparent',
                        borderRadius: isCurrentStep ? '12px' : 0,
                        transition: 'background-color 0.3s ease',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.5, borderRadius: 2, width: '100%', height: '100%' }}>
                        {/* Step icon */}
                        <Box
                          sx={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            padding: '8px',
                            gap: '10px',
                            bgcolor: isTransitMode ? '#FAE9DA' : `${modeColor}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isWalking ? '#d97706' : '#1976d2',
                            fontSize: '12px',
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        >
                          {isTransitMode ? (
                            <i className={`fa-solid ${transitIcon}`} style={{ color: '#D25529' }} />
                          ) : directionIcon.type === 'icon' ? (
                            <i className={`fa-solid ${directionIcon.value}`} style={{ color: modeColor, fontSize: '14px' }} />
                          ) : (
                            <span style={{ color: modeColor, fontSize: '12px', fontWeight: 700 }}>{directionIcon.value}</span>
                          )}
                        </Box>

                        {/* Step details */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: '"SF Pro Text", Inter, system-ui, Arial, sans-serif',
                              fontWeight: 400,
                              fontStyle: 'normal',
                              fontSize: '14px',
                              lineHeight: '20px',
                              letterSpacing: '0px',
                              mb: 0.5,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}
                            dangerouslySetInnerHTML={{ __html: instruction }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: '"SF Pro Text", Inter, system-ui, Arial, sans-serif',
                                fontWeight: 400,
                                fontStyle: 'normal',
                                fontSize: '12px',
                                lineHeight: '16px',
                                letterSpacing: '0px',
                                color: '#999',
                              }}
                            >
                              {step.distance?.text} • {step.duration?.text}
                              {step.travel_mode === 'TRANSIT' && step.transit_details?.departure_time && (() => {
                                const depInfo = getDepartureInfo(step);
                                if (!depInfo) return null;
                                const dateStr = depInfo.isToday
                                  ? ''
                                  : depInfo.isTomorrow
                                    ? 'Tomorrow '
                                    : `${depInfo.date.toLocaleDateString()} `;
                                const alts = (step as any).alternativeDepartures as Array<{ departureText?: string }> | undefined;
                                const altText = alts && alts.length > 0
                                  ? ', ' + alts.map((a) => a.departureText).filter(Boolean).join(', ')
                                  : '';
                                return (
                                  <Box component="span" sx={{ color: '#F57C00', fontWeight: 500 }}>
                                    {' '}• Departs {dateStr}{depInfo.text}{altText}
                                  </Box>
                                );
                              })()}
                            </Typography>
                            {/* Twinkling green dot for current step */}
                            {isCurrentStep && (
                              <Box
                                sx={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: '#4CAF50',
                                  animation: 'twinkle 1.5s ease-in-out infinite',
                                  '@keyframes twinkle': {
                                    '0%, 100%': {
                                      opacity: 1,
                                      boxShadow: '0 0 6px 2px rgba(76, 175, 80, 0.6)',
                                    },
                                    '50%': {
                                      opacity: 0.4,
                                      boxShadow: '0 0 3px 1px rgba(76, 175, 80, 0.3)',
                                    },
                                  },
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Location button for current step - fit bounds to step start/end */}
                        {isCurrentStep && step.start_location && step.end_location && (
                          <IconButton
                            aria-label={t('accessibility.show_step_on_map') || 'Show step on map'}
                            onClick={(e) => {
                              e.stopPropagation();
                              const startLat = step.start_location.lat;
                              const startLng = step.start_location.lng;
                              const endLat = step.end_location.lat;
                              const endLng = step.end_location.lng;
                              const south = Math.min(startLat, endLat);
                              const north = Math.max(startLat, endLat);
                              const west = Math.min(startLng, endLng);
                              const east = Math.max(startLng, endLng);
                              setFitBoundsRequest({
                                bounds: [[south, west], [north, east]],
                                timestamp: Date.now(),
                              });
                            }}
                            sx={{
                              width: '32px',
                              height: '32px',
                              bgcolor: '#FAE9DA',
                              color: '#D25529',
                              flexShrink: 0,
                              '&:hover': {
                                bgcolor: '#F5DBC8',
                              },
                            }}
                          >
                            <i className="fa-solid fa-location-crosshairs" style={{ fontSize: '14px' }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                    <Divider sx={{ width: '100%', display: isLastInFiltered ? 'none' : 'block' }} />
                  </>
                );
              })}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
  // Drag handle is now inside contentBox, so the same JSX serves both desktop + mobile
  return contentBox;
};
